import i18n from '@/utils/i18n';
import MenuHorizontalIcon from '@assets/icons/menu-horizontal.svg';
import React, { useRef, useState } from 'react';
import { ActionSheetContent } from '@/components/action-sheet';
import { Button } from '@/components/button';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import {
  DELETE_MENU_ITEM_MUTATION,
  DUPLICATE_MENU_ITEM_MUTATION,
  RENAME_MENU_ITEM_MUTATION,
} from '@/contexts/menu-info-context-queries';
import {
  MenuInfo_DeleteMenuItemMutation,
  MenuInfo_DeleteMenuItemMutationVariables,
  MenuInfo_DuplicateMenuItemMutation,
  MenuInfo_DuplicateMenuItemMutationVariables,
  MenuInfo_RenameMenuItemMutation,
  MenuInfo_RenameMenuItemMutationVariables,
} from '@/types';
import { MenuItem, useMenuInfoContext } from '@/contexts/menu-info-context';
import { MenuItemNameContent } from './menu-item-name-content';
import { StyleSheet, View } from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMutation } from '@apollo/client';
import { useToasts } from '@/contexts/toast-context';

const ACTIONS_BUTTON_RIGHT_OFFSET = 12;
const ACTIONS_POPOVER_WIDTH = 136;

enum MenuItemActionRoute {
  Delete = 'DELETE',
  Duplicate = 'DUPLICATE',
  Options = 'OPTIONS',
  Rename = 'RENAME',
}

interface Props {
  menuItem: MenuItem;
  onDelete: () => void;
}

export const MenuItemActions: React.FC<Props> = ({ menuItem, onDelete }) => {
  const itemQueryVariables = useItemQueryVariables();
  const { isSmallLayout } = useLayoutContext();
  const { showErrorToast } = useToasts();
  const { updateCachedMenuItems, menuItemMap } = useMenuInfoContext();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useAuthenticatedUserContext();

  const [showActions, setShowActions] = useState(false);
  const buttonRef = useRef<View>(null);

  const itemName = menuItem.name;
  const menuItemUUID = menuItem.uuid;

  const [renameMenuItem, { loading: renameLoading }] = useMutation<
    MenuInfo_RenameMenuItemMutation,
    MenuInfo_RenameMenuItemMutationVariables
  >(RENAME_MENU_ITEM_MUTATION);

  const [deleteMenuItem, { loading: deleteLoading }] = useMutation<
    MenuInfo_DeleteMenuItemMutation,
    MenuInfo_DeleteMenuItemMutationVariables
  >(DELETE_MENU_ITEM_MUTATION, {
    update: (client, { data }, context) => {
      if (data?.deleteMenuItem.success) {
        const deletedMenuItemUUID = context.variables!.menuItemUUID;
        updateCachedMenuItems(client, (menuItems) => {
          return menuItems.filter((mi) => mi.uuid !== deletedMenuItemUUID);
        });
      }
    },
  });

  const [duplicateMenuItem, { loading: duplicateLoading }] = useMutation<
    MenuInfo_DuplicateMenuItemMutation,
    MenuInfo_DuplicateMenuItemMutationVariables
  >(DUPLICATE_MENU_ITEM_MUTATION, {
    update: (client, { data }) => {
      const dupMenuItem = data?.duplicateMenuItem.menu_item;
      if (dupMenuItem) {
        updateCachedMenuItems(client, (menuItems) => [
          ...menuItems,
          dupMenuItem,
        ]);
      }
    },
  });

  return (
    <>
      <Button
        Icon={MenuHorizontalIcon}
        onPress={() => setShowActions(true)}
        ref={buttonRef}
        size="small"
        style={styles.button}
        trackingData={null}
        type="tertiary"
      />
      {showActions ? (
        <CombinedActionSheet
          animateTransitions
          initialRoute={MenuItemActionRoute.Options}
          onClose={() => setShowActions(false)}
          popoverStyle={(_, targetPosition) => {
            return {
              minWidth: ACTIONS_POPOVER_WIDTH,
              right: targetPosition.width - ACTIONS_BUTTON_RIGHT_OFFSET,
              top: targetPosition.height + targetPosition.y - 8,
            };
          }}
          popoverTarget={buttonRef.current}
          renderScene={({ jumpTo, routeKey }) => {
            switch (routeKey) {
              case MenuItemActionRoute.Delete:
                return (
                  <ActionSheetContent
                    actions={[i18n.t('delete'), i18n.t('cancel')]}
                    loadingActionIndex={deleteLoading ? 0 : undefined}
                    message={i18n.t('menu_item.delete_message', { itemName })}
                    onAction={async (_, i) => {
                      if (i === 0) {
                        try {
                          await deleteMenuItem({
                            variables: {
                              menuItemUUID,
                              restaurantUUID,
                              supplierUUID,
                            },
                          });
                          onDelete();
                        } catch {
                          showErrorToast();
                        }
                      }
                      setShowActions(false);
                    }}
                    title={i18n.t('menu_item.delete_title', { itemName })}
                  />
                );
              case MenuItemActionRoute.Options:
                return (
                  <View
                    style={isSmallLayout ? styles.actions : styles.actionsLarge}
                  >
                    {[
                      MenuItemActionRoute.Rename,
                      MenuItemActionRoute.Duplicate,
                      MenuItemActionRoute.Delete,
                    ].map((route) => (
                      <Button
                        key={route}
                        onPress={() => jumpTo(route)}
                        size={isSmallLayout ? 'medium' : 'small'}
                        trackingData={{
                          name: `menu_item_actions.${route.toLowerCase()}`,
                          properties: {
                            menuItemUUID,
                          },
                        }}
                        type={
                          route === MenuItemActionRoute.Delete
                            ? 'danger'
                            : 'tertiary'
                        }
                      >
                        {i18n.t(route.toLowerCase())}
                      </Button>
                    ))}
                  </View>
                );
              case MenuItemActionRoute.Rename:
                return (
                  <MenuItemNameContent
                    action={i18n.t('save')}
                    initialName={itemName}
                    loading={renameLoading}
                    onAction={async (newItemName) => {
                      try {
                        await renameMenuItem({
                          variables: {
                            itemName: newItemName,
                            menuItemUUID,
                          },
                        });
                        setShowActions(false);
                      } catch {
                        showErrorToast();
                      }
                    }}
                    title={i18n.t('edit_menu_item')}
                  />
                );
              case MenuItemActionRoute.Duplicate:
                return (
                  <MenuItemNameContent
                    action={i18n.t('duplicate')}
                    initialName={`${itemName} ${i18n.t('copy')}`}
                    loading={duplicateLoading}
                    onAction={async (dupItemName) => {
                      if (
                        Array.from(menuItemMap.values())
                          .map((mi) => mi.name.toLowerCase())
                          .includes(dupItemName.toLowerCase())
                      ) {
                        showErrorToast(
                          i18n.t('menu_item.duplicate_name_error'),
                        );
                        return;
                      }
                      try {
                        await duplicateMenuItem({
                          variables: {
                            ...itemQueryVariables,
                            menuItemName: dupItemName,
                            menuItemUUID,
                            restaurantUUID,
                            supplierUUID,
                          },
                        });
                        setShowActions(false);
                      } catch {
                        showErrorToast();
                      }
                    }}
                    title={i18n.t('duplicate_menu_item')}
                  />
                );
              default:
                return null;
            }
          }}
          routes={{
            [MenuItemActionRoute.Delete]: {
              previousKey: null,
            },
            [MenuItemActionRoute.Duplicate]: {
              previousKey: null,
            },
            [MenuItemActionRoute.Options]: {
              previousKey: null,
              renderAsPopover: true,
            },
            [MenuItemActionRoute.Rename]: {
              previousKey: null,
            },
          }}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  actions: {
    gap: 8,
  },
  actionsLarge: {
    gap: 12,
    paddingVertical: 8,
  },
  button: {
    marginRight: -ACTIONS_BUTTON_RIGHT_OFFSET,
  },
});
